import { useCallback } from "react";
import { useMutation } from "@apollo/client";

import { RESPONSE_CREATE } from "../../../graphql/mutations/Response";
import { QuizAnswerOption, QuizQuestion } from "../../../gql/graphql";

interface UseQuizCompletionProps {
  gameId: string;
  questions: QuizQuestion[];
  isPreviewMode: boolean;
  quizSessionId: string | null;
  startTime: string | null;
  completeSession: (sessionId: string) => Promise<void>;
}

export const useQuizCompletion = ({
  gameId,
  questions,
  isPreviewMode,
  quizSessionId,
  startTime,
  completeSession,
}: UseQuizCompletionProps) => {
  const [createResponse] = useMutation(RESPONSE_CREATE);

  const handleQuizComplete = useCallback(
    async (finalAnswers: QuizAnswerOption[]) => {
      if (isPreviewMode) {
        return;
      }

      try {
        await createResponse({
          variables: {
            data: {
              gameId,
              quizScores: finalAnswers.map((answer, index) => ({
                quizAnswerOptionId: answer.id,
                quizQuestionId: questions[index].id,
              })),
              endTime: new Date().toISOString(),
              startTime: startTime || new Date().toISOString(),
            },
          },
        });

        if (quizSessionId) {
          await completeSession(quizSessionId);
        }
      } catch (error) {
        console.error("Failed to save quiz response:", error);
      }
    },
    [gameId, questions, startTime, createResponse, isPreviewMode, quizSessionId, completeSession]
  );

  return {
    handleQuizComplete,
  };
};